import {
  combineReducers,
  configureStore,
  type UnknownAction,
} from "@reduxjs/toolkit";
import type { QueryClient } from "@tanstack/react-query";
import { queryClient } from "../../queryClient";
import { reducer as fieldRoutesReducer } from "./fieldroutesIntegration";
import { reducer as houseCallProReducer } from "./houseCallProIntegration";
import { reducer as serviceFusionReducer } from "./serviceFusionIntegration";
import { reducer as serviceTitanReducer } from "./serviceTitanIntegration";
import { reducer as sessionReducer, sessionSlice } from "./session";
import {
  createSessionCacheMiddleware,
  hasSession,
  sessionIdentityChanged,
} from "./sessionCacheMiddleware";
import { createSessionLifecycleMiddleware } from "./sessionLifecycleMiddleware";
import { reducer as uiReducer } from "./ui";

const combinedReducer = combineReducers({
  fieldRoutes: fieldRoutesReducer,
  serviceTitan: serviceTitanReducer,
  houseCallPro: houseCallProReducer,
  serviceFusion: serviceFusionReducer,
  session: sessionReducer,
  ui: uiReducer,
});

export type RootState = ReturnType<typeof combinedReducer>;

const shouldResetIntegrationWorkflows = (
  previousState: RootState,
  nextState: RootState,
  action: UnknownAction,
): boolean => {
  if (sessionSlice.actions.setCompanyId.match(action)) {
    return previousState.session.companyId !== nextState.session.companyId;
  }

  if (sessionSlice.actions.login.match(action)) {
    return true;
  }

  if (sessionSlice.actions.logout.match(action)) {
    return hasSession(previousState.session);
  }

  return (
    sessionSlice.actions.syncAuthFromStorage.match(action) &&
    sessionIdentityChanged(previousState.session, nextState.session)
  );
};

const resetIntegrationWorkflows = (state: RootState): RootState => ({
  ...state,
  fieldRoutes: fieldRoutesReducer(undefined, { type: "store/initialize" }),
  serviceTitan: serviceTitanReducer(undefined, { type: "store/initialize" }),
  houseCallPro: houseCallProReducer(undefined, { type: "store/initialize" }),
  serviceFusion: serviceFusionReducer(undefined, { type: "store/initialize" }),
});

/**
 * Keep tenant-bound client workflows aligned with the session transition.
 * Resetting in the root reducer makes the company change and all four workflow
 * resets one atomic Redux update, including loader- and cross-tab-driven paths.
 */
export const rootReducer = (
  previousState: RootState | undefined,
  action: UnknownAction,
): RootState => {
  const nextState = combinedReducer(previousState, action);
  if (
    !previousState ||
    !shouldResetIntegrationWorkflows(previousState, nextState, action)
  ) {
    return nextState;
  }

  return resetIntegrationWorkflows(nextState);
};

type CreateAppStoreOptions = {
  queryClient: QueryClient;
  preloadedState?: RootState;
};

/** Build an isolated app store while preserving the production middleware stack. */
export const createAppStore = ({
  queryClient: appQueryClient,
  preloadedState,
}: CreateAppStoreOptions) =>
  configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        createSessionCacheMiddleware(appQueryClient),
        createSessionLifecycleMiddleware(),
      ),
  });

export const store = createAppStore({
  queryClient,
});

export type AppStore = ReturnType<typeof createAppStore>;
export type AppDispatch = AppStore["dispatch"];
